import { defineComponent as _defineComponent } from 'vue'

import type { MetaInfo } from 'vue-meta/types/vue-meta';

const __default__ = {
	// eslint-disable-next-line vue/multi-word-component-names
	name: 'TitleDetail',
	metaInfo() {
		return (this as unknown as { head: MetaInfo }).head;
	},
};

import type { Route } from 'vue-router';
import type { AssertByTypename } from '@/helpers/graphql-model-helper';
import type { Content } from '@/interfaces/components/content';
import type { SnowplowContext, SnowplowModuleContext } from '@/helpers/tracking/providers/snowplow-contexts';
import type { TitleDetail } from '@/interfaces/title-details-graphql';

import { AvailabilityBadgeType } from '@/components/poster/types/availability-badge';

import { diffInDays, diffInMilliseconds } from '@/helpers/date-helper';
import { TrackingHelper } from '@/helpers/tracking/tracking-helper';
import { TitleContentType } from '@/interfaces/snowplow/title-context';
import { useQuery } from '@/helpers/composables/useApollo';
import { useSendBidId } from '@/components/sponsored-recommendations/useSendBidId';
import { useTitleDetailInfo } from '@/pages/titleDetailV1/v1/composables/useTitleDetailInfo';

import { ObjectType, Platform, SraPlacement } from '@/@types/graphql-types';

import {
	GetTitleDetailArticleDocument,
	GetTitleDetailArticleQuery,
	GetTitleDetailArticleQueryVariables,
} from '@/pages/graphql/queries/GetTitleDetailArticle.query';
import {
	GetUrlTitleDetailsDocument,
	GetUrlTitleDetailsQuery,
	GetUrlTitleDetailsQueryVariables,
} from '@/pages/graphql/queries/GetUrlTitleDetails.query';
import {
	GetNodeTitleDetailsDocument,
	GetNodeTitleDetailsQuery,
	GetNodeTitleDetailsQueryVariables,
} from '@/pages/graphql/queries/GetNodeTitleDetails.query';

import { ModalHelper } from '@/helpers/modal-helper';

import TitleDetailV1 from '@/pages/titleDetailV1/v1/TitleDetailV1.vue';
import SponsoredRecommendation from '@/components/sponsored-recommendations/SponsoredRecommendation.vue';

import { excludePackages } from '@/constants/buybox-excluded-packages.constant';

import { captureMessageForSentry } from '@/helpers/sentry-helper';
import { getRichMetaDescriptions } from '@/components/experiments/TitleMetaDescriptions/ctrExperimentData';
import { INTERNATIONAL_OFFERS_ENABLED_COUNTRIES } from '@/features/international-offers/international-offers-constants';

import {
	useConstantStore,
	useLanguageStore,
	useMetaStore,
	useRoutingStore,
	useTitleDetailsStore,
	useTrackingStore,
	useUserStore,
} from '@/helpers/composables/useStores';

import { useRoute } from 'vue-router/composables';
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { extractAVODOffer } from '@/helpers/title-helper';
import { generateUUIDv4 } from '@/helpers/uuid-helper';


export default /*#__PURE__*/_defineComponent({
  ...__default__,
  props: {
    entityId: null
  },
  emits: ["closeTitleDetailModal"],
  setup(__props: any, { expose }: { emit: ({
	(e: 'closeTitleDetailModal'): void;
}), expose: any, slots: any, attrs: any }) {

const props = __props as { entityId?: string };

const PlexPlayerModal = () => import('@/ui-components/PlexPlayerModal.vue');
const OldTitleDetail = () => import('@/pages/OldTitledetail.vue');

const EPISODE_MAX_LIMIT = 20;





const { activeRoute } = useRoutingStore();
const { setTitleJsonLD } = useMetaStore();
const { genresByShortName } = useConstantStore();
const { country, language } = useLanguageStore();
const { isLoggedIn, isProUser, settings, showOldTitlePage } = useUserStore();
const { setOverridePageState } = useTrackingStore();

const {
	refferalModuleContext,
	setSponsoredRecommendation,
	setTitleContext,
	clearTitleContext,
	setPermanentAudiences,
	resetPermanentAudiences,
	setLastVisitedTitleDetails,
} = useTitleDetailsStore();

const route = useRoute();

const content = ref<Content | null>(null);
function scrollToTop() {
	content.value?.scrollToTop();
}

const showNewTitlePage = computed(() => {
	if (isLoggedIn.value) {
		return !showOldTitlePage.value;
	}

	return true;
});

const titleDetailInModal = computed(() => props.entityId != null);

// by fullpath
const titleDetailsFullPathQuery = useQuery<GetUrlTitleDetailsQuery, GetUrlTitleDetailsQueryVariables>(
	GetUrlTitleDetailsDocument,
	() => ({
		fullPath: route.path,
		language: language.value,
		country: country.value,
		episodeMaxLimit: EPISODE_MAX_LIMIT,
		platform: Platform.Web,
		excludePackages: excludePackages(isLoggedIn.value, props.entityId || '', country.value, isProUser.value),
		fallbackToForeignOffers: INTERNATIONAL_OFFERS_ENABLED_COUNTRIES.includes(country.value),
	}),
	() => ({ enabled: !titleDetailInModal.value && activeRoute.value?.meta?.tab === 'detail', errorPolicy: 'all' })
);

titleDetailsFullPathQuery.onResult(result => {
	if (result.partial !== true) onTitleDetailNodeChange();
	if (result.errors && result.errors.length) {
		// report each partial error individually
		result.errors.forEach(err =>
			captureMessageForSentry(
				'[GraphQL Get Title Details By fullPath (partial)]:',
				{ error: err, where: 'Component: TitleDetail' },
				'error'
			)
		);
	}
});

titleDetailsFullPathQuery.onError(error => {
	captureMessageForSentry(
		'[GraphQL Get Title Details By fullPath]:',
		{ error: error, where: 'Component: TitleDetail' },
		'error'
	);
});

// by node id
const titleDetailsNodeQuery = useQuery<GetNodeTitleDetailsQuery, GetNodeTitleDetailsQueryVariables>(
	GetNodeTitleDetailsDocument,
	() =>
		({
			fullPath: route.path,
			entityId: props.entityId, // 'tm1141995', // rebel moon
			language: language.value,
			country: country.value,
			episodeMaxLimit: EPISODE_MAX_LIMIT,
			platform: Platform.Web,
			excludePackages: excludePackages(isLoggedIn.value, props.entityId || '', country.value, isProUser.value),
			fallbackToForeignOffers: INTERNATIONAL_OFFERS_ENABLED_COUNTRIES.includes(country.value),
		} as GetNodeTitleDetailsQueryVariables),
	() => ({ enabled: titleDetailInModal.value && !!props.entityId, errorPolicy: 'all' })
);

titleDetailsNodeQuery.onResult(result => {
	if (result.partial !== true) onTitleDetailNodeChange();

	if (result.errors && result.errors.length) {
		// report each partial error individually
		result.errors.forEach(err =>
			captureMessageForSentry(
				'[GraphQL Get Title Details By Node ID (partial)]:',
				{ error: err, where: 'Component: TitleDetail' },
				'error'
			)
		);
	}
});

titleDetailsNodeQuery.onError(error => {
	captureMessageForSentry(
		'[GraphQL Get Title Details By Node ID]:',
		{ error: error, where: 'Component: TitleDetail' },
		'error'
	);
});

const titleDetailNode = computed(() => {
	const node = titleDetailInModal.value
		? (titleDetailsNodeQuery?.result.value?.node as TitleDetail)
		: (titleDetailsFullPathQuery?.result.value?.urlV2.node as TitleDetail);

	const titleEntityId = node?.__typename === 'Season' ? node.show.id : node?.id;
	const excludedPackages = excludePackages(isLoggedIn.value, titleEntityId, country.value, isProUser.value);

	return {
		...node,
		offers: node?.offers.filter(offer => !excludedPackages.includes(offer.package.shortName)) ?? [],
	};
});

/* LOADING */
// is the title detail page in loading mode for:
// - requests
const isDataLoading = computed(() => titleDetailsFullPathQuery?.loading.value || titleDetailsNodeQuery?.loading.value);

// Used to show skeleton while buybox above the fold is loading to prevent CLS.
// Could be used for similar cases with other components
const isReadyToRender = ref(true);

// for INP improvements:
// we need an immediate change upon userinteraction.
// the biggest user interaction on title detail pages (~40% user interactions) is navigating from title detail to another title detail, and it's the least performant, too.
// this `perceivedLoading` will briefly (0ms) v-if in and out. so the children elements don't take up all of our performance budget.
const perceivedLoading = ref(false);

// is the title detail page in loading mode for:
// - requests
// - async components loading (note: if we don't wait for async component loading, the CLS will increase)
// - artificial perceived performance after a user interaction (for INP reasons)
const isLoading = computed(() => isDataLoading.value || !isReadyToRender.value || perceivedLoading.value);

const isPerceivedLoading = computed(() => !titleDetailNode.value || perceivedLoading.value);

const { titleName, titleId, titleObjectId, titleObjectType } = useTitleDetailInfo(titleDetailNode);

const { sponsoredAd } = useSendBidId({
	pageType: 'VIEW_TITLE_DETAIL',
	placement: SraPlacement.DetailPage,
	jwEntityID: () => props.entityId ?? titleId.value,
	isEnabled: () => !isDataLoading.value,
});

const isListSponsoredAd = computed(() => sponsoredAd.value?.campaign?.node?.__typename === 'GenericTitleList');

const isCampaignWithinSimiliarTitles = computed(() => {
	return sponsoredAd.value?.holdoutGroup == false && sponsoredAd.value?.campaign != null && !isListSponsoredAd.value;
});

function onTitleDetailNodeChange() {
	if (!titleDetailNode.value) return;

	// reset state
	clearTitleContext();

	const isSeason = titleDetailNode.value.__typename === 'Season' && !!titleDetailNode.value.show;
	setTitleContext({
		jwEntityId: titleId.value,
		titleId: titleObjectId.value,
		objectType: titleObjectType.value,
		seasonNumber: isSeason ? titleDetailNode.value.content.seasonNumber : undefined,
		episodeNumber: undefined,
		contentType: titleContentType.value,
	});

	// save last visited titleId for search params
	setLastVisitedTitleDetails({
		titleId: titleId.value,
		titleName: titleName.value,
		isTitleUpcoming: !titleDetailNode.value.content.isReleased,
		offers: titleDetailNode.value.offers,
		fullPath: titleDetailNode.value.content.fullPath,
	});

	// set permanent audiences
	if (titleDetailNode.value.__typename !== 'Season') {
		setPermanentAudiences(titleDetailNode.value.permanentAudiences);
	}

	if (!titleDetailInModal.value) {
		// set meta
		setTitleJsonLD({ title: titleDetailNode.value });

		// make sure the pageview tracking event is added to the pooling on client only
		if (process.client) {
			TrackingHelper.trackPage(route.path, additionalContexts.value);
		}

		// ensure the modal state is reset
		setOverridePageState(null);
	} else {
		// we temporarily update the page_type_context when the TitleDetail is opened in a modal
		setOverridePageState({
			pageType: titleObjectType.value,
			pageViewUUID: generateUUIDv4(),
			pageEntryMode: 'modal',
		});
	}
}

const head = computed(() => {
	if (titleDetailNode.value == null) return {};

	return {
		meta: [
			{
				vmid: 'og:image',
				property: 'og:image',
				content: JW_CONFIG.IMAGESCALER_URL + titleDetailNode.value.content?.fullPosterUrl,
			},
			{
				vmid: 'og:type',
				property: 'og:type',
				content: titleObjectType.value === ObjectType.Movie ? 'video.movie' : 'video.tv_show',
			},
			{ vmid: 'og:image:width', property: 'og:image:width', content: '718' },
			{ vmid: 'og:image:height', property: 'og:image:height', content: '1024' },
			// CTR_META_DESCRIPTIONS_EXPERIMENTS
			...(ctrExperimentMetaDescriptionV2.value
				? [
						{
							vmid: 'description',
							name: 'description',
							content: ctrExperimentMetaDescriptionV2.value,
						},
						{
							vmid: 'og:description',
							property: 'og:description',
							content: ctrExperimentMetaDescriptionV2.value,
						},
				  ]
				: []),
			// CTR_META_DESCRIPTIONS_EXPERIMENTS
		],
	};
});

const promotedOffers = computed(() => titleDetailNode.value?.promotedOffers ?? []);

const additionalContexts = computed(() => {
	const refferedModuleContext: SnowplowModuleContext | null = refferalModuleContext.value;

	const contexts: SnowplowContext[] = [];
	if (refferedModuleContext) contexts.push(refferedModuleContext);

	return contexts;
});

const titleDetailsUrl = computed(() => {
	if (!titleDetailInModal.value) {
		const { node, ...rest } =
			(titleDetailsFullPathQuery?.result.value?.urlV2 as AssertByTypename<
				GetUrlTitleDetailsQuery['urlV2'],
				'Url'
			>) || {};

		return rest;
	}

	// either byFullPath or byNodeId (no if else to not make ts trip up on any optional chaining)
	return titleDetailsNodeQuery?.result.value?.node as AssertByTypename<GetNodeTitleDetailsQuery['node'], 'Url'>;
});

const metaData = computed(() => {
	if (!titleDetailsUrl.value) return undefined;

	return {
		...titleDetailsUrl.value,
		htmlContent: JSON.parse(titleDetailsUrl.value?.htmlContent || '{}'),
	};
});

/** exclude request if the page is opened through a modal (meaning the title doesn't have a fullpath) */
const enableSecondaryQueries = computed(
	() => !titleDetailInModal.value || route.name?.startsWith('app.detail') === true
);

const articleQuery = useQuery<GetTitleDetailArticleQuery, GetTitleDetailArticleQueryVariables>(
	GetTitleDetailArticleDocument,
	() => ({
		fullPath: route.path,
		language: language.value,
		country: country.value,
	}),
	{ errorPolicy: 'ignore', enabled: enableSecondaryQueries }
);

const article = computed(() => {
	const node = articleQuery.result.value?.urlV2.node;

	type Node = AssertByTypename<typeof node, 'Movie' | 'Show' | 'Season'> | undefined;
	return (articleQuery.result.value?.urlV2.node as Node)?.content?.articles?.[0] ?? null;
});

watch(isLoggedIn, () => {
	titleDetailsFullPathQuery?.refetch();
	titleDetailsNodeQuery?.refetch();
});

// when leaving page through modal
onBeforeUnmount(() => onLeaveTitleDetailPage());

// when leaving page through route
watch(activeRoute, (route: Route) => {
	const titleHasChanged =
		route.name?.includes('app.detail') && titleDetailNode.value?.content.fullPath !== route.path;

	if (titleHasChanged) {
		scrollToTop();
		// We need to reset title object, to prevent CLS issue;
		clearTitleContext();

		// navigating from (popular or title page) -> title detail page is the most expensive userinteraction.
		perceivedLoading.value = true;

		// using `setTimeout` instead of `$nextTick` here, because `$nextTick` will somehow be merged into the same render #vueoptimisations.
		// that will render the component only once which is more expensive because existing components will be updated instead of torn down and created.
		setTimeout(() => (perceivedLoading.value = false), 0);
	}

	if (!route.name?.includes('app.detail')) {
		onLeaveTitleDetailPage();
	}
});

function onLeaveTitleDetailPage() {
	resetPermanentAudiences();
	setSponsoredRecommendation(null);
}

const upcomingReleaseFromSelectedPackages = computed(() => {
	const userPackages = settings.value.providers;

	return (
		titleDetailNode.value.content.upcomingReleases
			?.filter(item => diffInMilliseconds(new Date(item.releaseDate)) > 0)
			.filter(item => (item?.releaseCountDown ?? diffInDays(new Date(item.releaseDate))) < 30)
			.find(item => item.package?.shortName && userPackages.includes(item.package?.shortName)) || null
	);
});

const leavingOfferFromSelectedPackages = computed(() => {
	const userPackages = settings.value.providers;

	return (
		titleDetailNode.value.availableTo
			?.filter(item => (item?.availableCountDown ?? diffInDays(new Date(item.availableToDate))) < 30)
			.find(item => userPackages.includes(item.package?.shortName)) || null
	);
});

const titleContentType = computed(() => {
	const availabilityBadgeType = !!upcomingReleaseFromSelectedPackages.value
		? AvailabilityBadgeType.Upcoming
		: AvailabilityBadgeType.Leaving;

	const availabilityBadgeDate =
		upcomingReleaseFromSelectedPackages.value?.releaseDate ??
		leavingOfferFromSelectedPackages.value?.availableToDate ??
		null;

	if (isLoggedIn.value && availabilityBadgeDate != null) {
		return availabilityBadgeType === AvailabilityBadgeType.Upcoming
			? TitleContentType.ComingSoon
			: TitleContentType.LeavingSoon;
	}

	return undefined;
});

const AVODOffer = computed(() => extractAVODOffer(titleDetailNode.value));

onMounted(async () => {
	setTimeout(() => {
		const query = activeRoute.value.query;

		if (AVODOffer.value && query['package'] === 'plc') {
			ModalHelper.openModal(
				PlexPlayerModal,
				{ offer: AVODOffer.value, title: titleDetailNode.value },
				{ cssClass: 'trailer-modal' },
				'',
				true,
				[]
			);
		}
	});

	await nextTick();
});

// UPDATE_META_DESCRIPTIONS
const ctrExperimentMetaDescriptionV2 = computed(() => {
	if (country.value !== 'DE' || titleDetailNode.value?.content?.fullPath == null) return null;
	const path = titleDetailNode.value.content.fullPath;

	return getRichMetaDescriptions(titleDetailNode.value.content.title, path);
});
// UPDATE_META_DESCRIPTIONS

expose({ head });

return { __sfc: true,PlexPlayerModal, OldTitleDetail, EPISODE_MAX_LIMIT, props, activeRoute, setTitleJsonLD, genresByShortName, country, language, isLoggedIn, isProUser, settings, showOldTitlePage, setOverridePageState, refferalModuleContext, setSponsoredRecommendation, setTitleContext, clearTitleContext, setPermanentAudiences, resetPermanentAudiences, setLastVisitedTitleDetails, route, content, scrollToTop, showNewTitlePage, titleDetailInModal, titleDetailsFullPathQuery, titleDetailsNodeQuery, titleDetailNode, isDataLoading, isReadyToRender, perceivedLoading, isLoading, isPerceivedLoading, titleName, titleId, titleObjectId, titleObjectType, sponsoredAd, isListSponsoredAd, isCampaignWithinSimiliarTitles, onTitleDetailNodeChange, head, promotedOffers, additionalContexts, titleDetailsUrl, metaData, enableSecondaryQueries, articleQuery, article, onLeaveTitleDetailPage, upcomingReleaseFromSelectedPackages, leavingOfferFromSelectedPackages, titleContentType, AVODOffer, ctrExperimentMetaDescriptionV2, TitleDetailV1, SponsoredRecommendation }
}

})